import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { manualUrl } from "@/components/HelpButton";
import ChapterEditDialog from "./ChapterEditDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short" }) : "—");

export default function ChapterEntriesPanel({ refreshKey, onChanged }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get("/admin/help/chapter-entries")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const remove = async (entry) => {
    if (!window.confirm(`Paragraaf "${entry.title}" verwijderen uit de handleiding? Dit kan niet ongedaan worden gemaakt.`)) return;
    try {
      const { data: d } = await api.delete(`/admin/help/chapter-entries/${entry.id}`);
      toast.success(d.removed_from_manual ? "Paragraaf verwijderd uit de handleiding." : "Registratie verwijderd (de paragraaf stond niet meer in de handleiding).");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="chapter-entries-panel">
      <p className="text-[11px] text-muted-foreground px-3 py-2 border-b border-border">Paragrafen die via een kennisgat aan de handleiding zijn toegevoegd. Bewerken of verwijderen past de handleiding direct aan; het anker blijft gelijk.</p>
      {!data ? <div className="p-3 text-sm text-muted-foreground">Laden…</div> : data.entries.length === 0 ? (
        <div className="p-3 text-sm text-muted-foreground" data-testid="chapter-entries-empty">Nog geen paragrafen via de chat toegevoegd.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="chapter-entries-table">
            <thead className="bg-foreground text-background">
              <tr>{["Paragraaf", "Hoofdstuk", "Tekst", "Toegevoegd", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-display uppercase text-[10px] tracking-widest">{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.entries.map((e, i) => (
                <tr key={e.id} className="border-t border-border even:bg-muted/30" data-testid={`chapter-entry-row-${i}`}>
                  <td className="px-3 py-2 max-w-[260px] font-bold">
                    <a href={manualUrl(e.anchor)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" title={`#${e.anchor}`}>
                      <span className="line-clamp-2">{e.title}</span><ExternalLink className="w-3 h-3 shrink-0 text-muted-foreground" />
                    </a>
                    {e.source_question && <div className="text-[11px] font-normal text-muted-foreground truncate" title={e.source_question}>uit chat: {e.source_question}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{e.chapter_title || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2 max-w-[340px] text-xs text-muted-foreground"><div className="line-clamp-2" title={e.markdown}>{e.markdown}</div></td>
                  <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{fmt(e.added_at)}{e.updated_at && <div className="text-muted-foreground">bewerkt {fmt(e.updated_at)}</div>}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {e.in_manual ? <span className="inline-flex items-center gap-1 text-positive" data-testid={`chapter-entry-status-${i}`}><CheckCircle2 className="w-3.5 h-3.5" /> In handleiding</span>
                      : <span className="inline-flex items-center gap-1 text-destructive" title="Het anker staat niet (meer) in de handleiding" data-testid={`chapter-entry-status-${i}`}><AlertTriangle className="w-3.5 h-3.5" /> Niet gevonden</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(e)} disabled={!e.in_manual} className="p-1.5 hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed" title={e.in_manual ? "Bewerken" : "Niet bewerkbaar: paragraaf staat niet meer in de handleiding"} data-testid={`chapter-entry-edit-${i}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(e)} className="p-1.5 hover:bg-destructive/10 text-destructive" title="Verwijderen" data-testid={`chapter-entry-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ChapterEditDialog entry={editing} onClose={() => setEditing(null)} onSaved={() => { load(); onChanged?.(); }} />
    </div>
  );
}
